// src/api/safeRequest.ts
import axios from 'axios';
import { globalErrorStore } from '../stores/globalError.store.ts';

export type AppErrorType = 'fullScreen' | 'snackBar';

export class AppException extends Error {
  statusCode?: number;
  type: AppErrorType;

  constructor(message: string, opts?: { statusCode?: number; type?: AppErrorType }) {
    super(message);
    this.statusCode = opts?.statusCode;
    this.type = opts?.type ?? 'snackBar';
  }
}

export async function safeRequest<T>(
  request: () => Promise<T>,
  opts?: { handleGlobally?: boolean }
): Promise<T> {
  const handleGlobally = opts?.handleGlobally ?? true;

  try {
    return await request();
  } catch (e) {
    // axios 에러 표준화
    if (axios.isAxiosError(e)) {
      const statusCode = e.response?.status;
      const data = e.response?.data as any;

      let message = e.message ?? 'Unknown error';
      if (data?.message) message = String(data.message);

      const toGlobal = (ex: AppException) => {
        if (handleGlobally) globalErrorStore.getState().setError(ex);
        return ex;
      };

      if (!statusCode) throw toGlobal(new AppException('No response from server', { type: 'fullScreen' }));

      if (statusCode === 401) {
        throw toGlobal(new AppException(message, { statusCode, type: 'fullScreen' }));
      }

      if (statusCode >= 300 && statusCode < 500) {
        throw toGlobal(new AppException(message, { statusCode, type: 'snackBar' }));
      }

      if (statusCode >= 500) {
        throw toGlobal(new AppException('Server error', { statusCode, type: 'fullScreen' }));
      }

      throw toGlobal(new AppException(message, { statusCode, type: 'snackBar' }));
    }

    const ex = new AppException(`Unexpected error: ${String(e)}`, { type: 'fullScreen' });
    globalErrorStore.getState().setError(ex);
    throw ex;
  }
}
